/**
 * The project's parts list — what the Studio's System view shows — derived
 * from the room document, never stored beside it.
 *
 * A build is not a second document (ADR 011: "a build is derived, not
 * stored"): the speakers placed in the room ARE the speakers in the list, the
 * receiver on the media console IS the receiver in the list, the TV on the
 * wall IS the TV in the list. So this reads `doc.speakers`, `doc.gear` and
 * `doc.media` and groups them the way a parts list reads — by the job each
 * part does — and prices them at list price.
 *
 * Grouping follows the channels `checkBuild` derived from where the speakers
 * actually are (a speaker behind the seat is a surround, whatever it was
 * added as), so the list and the compatibility checks agree about what each
 * speaker is for. Without a report (the catalogue still loading) it falls
 * back to what the speaker says about itself.
 *
 * Displays are optional: a room without one shows none, and a display with no
 * model never counts as a part still to choose.
 */

import type { EditorDoc, MediaAspect, MediaKind, SpeakerEntry } from '@roomtreatment/core';
import type { Product } from '@roomtreatment/av-catalog';
import type { BuildReport, Issue } from './types';
import { channelGroup } from './channels';
import { displayRole, mediaProductId, type MediaPart } from './media';

export type SheetSlot = 'front' | 'center' | 'surround' | 'height' | 'subwoofer' | 'speakers' | 'electronics' | 'media';

/** The slots a speaker can be listed under — every one but the devices'. */
export type SpeakerSheetSlot = Exclude<SheetSlot, 'electronics' | 'media'>;

/** Top to bottom, the order a system is usually specified in. */
export const SHEET_SLOT_ORDER: readonly SheetSlot[] = [
  'front', 'center', 'surround', 'height', 'subwoofer', 'speakers', 'electronics', 'media',
];

/** Which half of which display a `media` row is, and the picture it makes —
 *  what the row is when no model is chosen, and what a model binds to. */
export interface SheetDisplay {
  part: MediaPart;
  kind: MediaKind;
  diagonalM: number;
  aspect: MediaAspect;
  /** A screen row for a projector that throws at nothing yet. */
  missing: boolean;
}

interface SheetRowBase {
  /** Stable across renders: the slot and the model (or "none"). */
  key: string;
  /** The chosen model, when the catalogue knows it. */
  product: Product | null;
  /** The bound model id, kept even when the catalogue does not know it, so a
   *  row never silently turns into "no model chosen". */
  productId: string | null;
  /** The speakers or devices this row stands for, in document order. */
  entityIds: string[];
  /** How many physical units: speakers, or devices. */
  units: number;
  /** List price for those units — a pair-sold model is bought in pairs — or
   *  null when there is no model, or no price, to add. */
  priceUsd: number | null;
  /** Every compatibility issue that names one of this row's units. */
  issues: Issue[];
}

/** A row of the parts list. A `media` row is one half of one display, and
 *  always says which (`display`); no other row has one. */
export type SheetRow =
  | (SheetRowBase & { slot: Exclude<SheetSlot, 'media'>; display?: undefined })
  | (SheetRowBase & { slot: 'media'; display: SheetDisplay });

export interface SystemSheet {
  rows: SheetRow[];
  /** Sum of every priced row. */
  totalUsd: number;
  /** Rows that add nothing to the total: no model chosen, or no known price.
   *  A display without a model is not one: a display is optional. */
  unpricedRows: number;
}

/** The slot one speaker is listed under — exported so a single speaker's
 *  card (the placement card's "Choose") names the same slot its System row
 *  does. Reads only the channels, so a caller can pass `deriveChannels`'
 *  result without running the whole `checkBuild`. */
export function speakerSlot(s: SpeakerEntry, report: Pick<BuildReport, 'channels'> | null): SpeakerSheetSlot {
  const channel = report?.channels[s.id];
  if (channel === 'LFE') return 'subwoofer';
  if (channel) {
    const group = channelGroup(channel);
    if (group === 'front') return 'front';
    if (group === 'center') return 'center';
    if (group === 'surround' || group === 'surround_back') return 'surround';
    if (group === 'height1' || group === 'height2') return 'height';
  }
  if (s.subkind === 'subwoofer') return 'subwoofer';
  if (s.subkind === 'overhead') return 'height';
  if (s.role === 'main_l' || s.role === 'main_r') return 'front';
  return 'speakers';
}

/** What `units` of a product cost at list price. */
export function listPriceUsd(product: Pick<Product, 'msrpUsd' | 'sellUnit'>, units: number): number {
  const lots = product.sellUnit === 'pair' ? Math.ceil(units / 2) : units;
  return lots * product.msrpUsd.v;
}

export function systemSheet(
  doc: EditorDoc,
  catalog: ReadonlyMap<string, Product>,
  report: BuildReport | null,
): SystemSheet {
  // Insertion-ordered, so rows keep the document's order within a slot.
  const groups = new Map<string, { slot: Exclude<SheetSlot, 'media'>; productId: string | null; ids: string[] }>();
  const add = (slot: Exclude<SheetSlot, 'media'>, productId: string | null, id: string) => {
    const key = `${slot}:${productId ?? 'none'}`;
    const g = groups.get(key);
    if (g) g.ids.push(id);
    else groups.set(key, { slot, productId, ids: [id] });
  };

  for (const s of doc.speakers) add(speakerSlot(s, report), s.productId ?? null, s.id);
  for (const g of doc.gear ?? []) {
    // A display saved as gear is on its way into media (adoptGearDisplays);
    // it is never electronics.
    const product = catalog.get(g.productId);
    if (product && displayRole(product)) continue;
    add('electronics', g.productId, g.id);
  }

  const issues = report?.issues ?? [];
  const issuesFor = (ids: ReadonlySet<string>) => issues.filter((i) => i.subjects.some((s) => ids.has(s)));
  const rows: SheetRow[] = [...groups.entries()].map(([key, g]) => {
    const product = g.productId ? catalog.get(g.productId) ?? null : null;
    return {
      key,
      slot: g.slot,
      product,
      productId: g.productId,
      entityIds: g.ids,
      units: g.ids.length,
      priceUsd: product ? listPriceUsd(product, g.ids.length) : null,
      issues: issuesFor(new Set(g.ids)),
    };
  });

  // Every display is its own row (two TVs are two places, not a pair), and a
  // projector setup is two: the projector, and the screen it throws at.
  for (const m of doc.media ?? []) {
    const parts: MediaPart[] = m.kind === 'projector' ? ['device', 'screen'] : ['device'];
    for (const part of parts) {
      const productId = mediaProductId(m, part);
      const product = productId ? catalog.get(productId) ?? null : null;
      rows.push({
        key: part === 'device' ? `media:${m.id}` : `media:${m.id}:screen`,
        slot: 'media',
        product,
        productId,
        entityIds: [m.id],
        units: 1,
        priceUsd: product ? listPriceUsd(product, 1) : null,
        // The setup's issues (PRJ-01 names the entry) belong to the projector.
        issues: part === 'device' ? issuesFor(new Set([m.id])) : [],
        display: {
          part, kind: m.kind, diagonalM: m.diagonalM, aspect: m.aspect,
          missing: part === 'screen' && !m.screen,
        },
      });
    }
  }
  rows.sort((a, b) => SHEET_SLOT_ORDER.indexOf(a.slot) - SHEET_SLOT_ORDER.indexOf(b.slot));

  return {
    rows,
    totalUsd: rows.reduce((sum, r) => sum + (r.priceUsd ?? 0), 0),
    unpricedRows: rows.filter((r) => r.priceUsd === null && r.slot !== 'media').length,
  };
}
